// ---- Types ----

export interface StageMapping {
  jobnimbusStage: string;
  jobnimbusStageId: string;
  paivStage: string;
  paivStageId: string;
}

export interface CustomField {
  name: string;
  label: string;
}

interface PaivStage {
  id: string;
  title?: string;
  sequence?: number;
}

interface JNStage {
  id: string;
  name: string;
  order?: number;
  numericId?: number;
}

// ---- Custom field helpers ----

/**
 * Normalizes raw JobNimbus custom field objects into { name, label } pairs.
 */
export function normalizeCustomFields(raw: any[]): CustomField[] {
  return (raw ?? [])
    .map((f: any) => ({
      name: f.field || f.name || f.field_name || '',
      label: f.title || f.label || f.name || f.field_name || '',
    }))
    .filter((f) => f.name);
}

/**
 * Converts stored field mapping values from labels to field names
 * using the resolved custom fields list.
 */
export function convertFieldMappingLabels(
  mappings: Record<string, string>,
  customFields: CustomField[]
): Record<string, string> {
  if (!customFields.length || !Object.keys(mappings).length) return mappings;

  const converted: Record<string, string> = {};
  for (const [paivField, stored] of Object.entries(mappings)) {
    if (typeof stored !== 'string') continue;
    const byLabel = customFields.find((f) => f.label === stored);
    if (byLabel) {
      converted[paivField] = byLabel.name;
    } else if (customFields.find((f) => f.name === stored)) {
      converted[paivField] = stored;
    }
  }
  return Object.keys(converted).length > 0 ? converted : mappings;
}

// ---- Sort helpers ----

/**
 * Sorts stage mappings by the Paiv stage sequence (for displaying saved data).
 */
export function sortMappingsByPaivSequence(
  mappings: StageMapping[],
  paivStages: PaivStage[]
): StageMapping[] {
  return [...mappings].sort((a, b) => {
    if (!a.paivStageId && !b.paivStageId) return 0;
    if (!a.paivStageId) return 1;
    if (!b.paivStageId) return -1;

    const stageA = paivStages.find((s) => s.id === a.paivStageId);
    const stageB = paivStages.find((s) => s.id === b.paivStageId);

    const seqA = stageA?.sequence ?? Number.MAX_SAFE_INTEGER;
    const seqB = stageB?.sequence ?? Number.MAX_SAFE_INTEGER;
    if (seqA !== seqB) return seqA - seqB;

    const titleA = stageA?.title || '';
    const titleB = stageB?.title || '';
    if (titleA !== titleB) return titleA.localeCompare(titleB);

    return 0;
  });
}

/**
 * Sorts stage mappings by JobNimbus stage order/numericId (for active mapping).
 */
export function sortMappingsByJobNimbusOrder(
  mappings: StageMapping[],
  jnStages: JNStage[]
): StageMapping[] {
  return [...mappings].sort((a, b) => {
    const stageA = jnStages.find(
      (s) => s.id === a.jobnimbusStageId || s.name === a.jobnimbusStage
    );
    const stageB = jnStages.find(
      (s) => s.id === b.jobnimbusStageId || s.name === b.jobnimbusStage
    );

    const orderA = stageA?.order;
    const orderB = stageB?.order;
    if (orderA !== undefined && orderB !== undefined) return orderA - orderB;
    if (orderA !== undefined) return -1;
    if (orderB !== undefined) return 1;

    const idA = stageA?.numericId;
    const idB = stageB?.numericId;
    if (idA !== undefined && idB !== undefined) return idA - idB;
    if (idA !== undefined) return -1;
    if (idB !== undefined) return 1;

    return 0;
  });
}
